import { memo, useCallback } from 'react';
import type { ClassFeature } from '../types';
import './ClassCard.css';

import barbarianIcon from '../../public/classes/barbarian.svg?url';
import bardIcon from '../../public/classes/bard.svg?url';
import clericIcon from '../../public/classes/cleric.svg?url';
import druidIcon from '../../public/classes/druid.svg?url';
import fighterIcon from '../../public/classes/fighter.svg?url';
import monkIcon from '../../public/classes/monk.svg?url';
import paladinIcon from '../../public/classes/paladin.svg?url';
import rangerIcon from '../../public/classes/ranger.svg?url';
import rogueIcon from '../../public/classes/rogue.svg?url';
import sorcererIcon from '../../public/classes/sorcerer.svg?url';
import warlockIcon from '../../public/classes/warlock.svg?url';
import wizardIcon from '../../public/classes/wizard.svg?url';
import artificerIcon from '../../public/classes/artificer.svg?url';

interface ClassCardProps {
  classFeature: ClassFeature;
  isSelected: boolean;
  onToggle: (id: string) => void;
}

const CLASS_ICONS: Record<string, string> = {
  Barbarian: barbarianIcon,
  Bard: bardIcon,
  Cleric: clericIcon,
  Druid: druidIcon,
  Fighter: fighterIcon,
  Monk: monkIcon,
  Paladin: paladinIcon,
  Ranger: rangerIcon,
  Rogue: rogueIcon,
  Sorcerer: sorcererIcon,
  Warlock: warlockIcon,
  Wizard: wizardIcon,
  Artificer: artificerIcon,
};

export const ClassCard = memo(function ClassCard({ classFeature, isSelected, onToggle }: ClassCardProps) {
  const handleToggle = useCallback(() => {
    onToggle(classFeature.id);
  }, [onToggle, classFeature.id]);

  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const classIcon = CLASS_ICONS[classFeature.className];

  return (
    <div
      className={`class-card-mini ${isSelected ? 'selected' : ''}`}
      onClick={handleToggle}
      data-class={classFeature.className.toLowerCase()}
    >
      <div className="class-card-mini-header">
        {classIcon ? (
          <img src={classIcon} alt={classFeature.className} className="class-card-icon" />
        ) : (
          <span className="class-card-icon" title={classFeature.className}>•</span>
        )}
        <h3>{classFeature.name}</h3>
      </div>
      <div className="class-card-mini-meta">
        <span className="class-card-level">{classFeature.className} {classFeature.level}</span>
        {classFeature.featureType === 'invocation' && (
          <span className="class-card-subclass">Eldritch Invocation</span>
        )}
        {classFeature.subclass && (
          <span className="class-card-subclass">{classFeature.subclass}</span>
        )}
      </div>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={handleToggle}
        onClick={handleCheckboxClick}
        className="class-card-checkbox"
        aria-label={`Select ${classFeature.name} for print`}
      />
    </div>
  );
});
